import React, {useMemo, useState, useEffect} from 'react';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import Chip from '@material-ui/core/Chip';
import FormControl from '@material-ui/core/FormControl';
import Grid from '@material-ui/core/Grid/Grid';
import { KeyboardArrowDown } from '@material-ui/icons';
import CloseIcon from '@material-ui/icons/Close';
import { useMfwThemeProvider } from 'myfitworld-utils';
import { useIntl } from 'react-intl';
import getLocalizedProperty from '../utils/getLocalizedProperty';
import {makeStyles} from "@material-ui/core/styles";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const useStyles = makeStyles({
  chip: {
    maxWidth: '100%',
    '& .MuiChip-label': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      fontSize: '0.875rem',
      '&.small': {
        fontSize: '0.625rem',
      }
    }
  }
});

const MultiSelectField = ({
  label,
  options,
  value,
  onChange,
  required = false,
  disabled,
  isMultiple,
  errors
} : {
  label: string,
  options: Array<any>,
  value?: Array<string> | string,
  onChange: (event: any) => void,
  required?: boolean,
  disabled?: boolean,
  isMultiple?: boolean,
  errors?: any
}) => {
    const {locale} = useIntl();
    const optionsMap = useMemo(() => options.reduce((acc: { [key: string]: string }, option) => {
    acc[option.id || ''] = option.name.en ? getLocalizedProperty(locale, option.name): option.name;
    return acc;
  }, {}), [options, locale]);
  const {theme} = useMfwThemeProvider();
  const classes = useStyles();
  
  const [isOpen, setOpen]=useState<boolean>(false);
  const [smallFont, setSmallFont] = useState<{[key: string]: boolean}>({});
  
  const measureTextWidth = (text: string, chipId: string) => {
    const tempSpan = document.createElement('span');
    tempSpan.style.visibility = 'hidden';
    tempSpan.style.position = 'absolute';
    tempSpan.style.fontSize = '0.875rem';
    tempSpan.style.fontFamily = 'Roboto, sans-serif';
    tempSpan.innerText = text;
    document.body.appendChild(tempSpan);
    
    const textWidth = tempSpan.offsetWidth;
    document.body.removeChild(tempSpan);
    
    // If chip has parent with width constraint, check if text fits
    const chipMaxWidth = 100; // Approximate max width of a chip
    if (textWidth > chipMaxWidth) {
      setSmallFont(prev => ({...prev, [chipId]: true}));
    }
  };

  useEffect(() => {
    if (value) {
      if (Array.isArray(value)) {
        value.forEach(val => {
          if (optionsMap[val]) {
            measureTextWidth(optionsMap[val], val);
          }
        });
      } else if (typeof value === 'string' && optionsMap[value]) {
        measureTextWidth(optionsMap[value], value);
      }
    }
  }, [value, optionsMap]);

  return (
    <FormControl
      variant="filled"
      required={required}
      fullWidth
      margin="dense"
      error={errors && !!errors.category}
    >
      <InputLabel>
        {label}
      </InputLabel>
      <Select
        multiple={isMultiple}
        renderValue={(selected) => (
          <Grid container spacing={1}>
            {Array.isArray(selected) && (selected as string[])
              .filter(option => optionsMap[option] !== undefined)
              .map((value) => (
                <Grid item key={value}>
                  <Chip 
                    label={optionsMap[value]} 
                    className={classes.chip}
                    classes={{
                      label: smallFont[value] ? 'small' : ''
                    }}
                  />
                </Grid>
              ))
            }
            {typeof(selected) === 'string' && (selected as string) && 
              optionsMap[selected] !== undefined &&
              (
                <Grid item key={selected}>
                  <Chip 
                    label={optionsMap[selected]} 
                    className={classes.chip}
                    classes={{
                      label: smallFont[selected] ? 'small' : ''
                    }}
                  />
                </Grid>
              )
            }     
          </Grid>
        )}
        value={value || []}
        MenuProps={MenuProps}
        onOpen={()=>{setOpen(true)}} 
        onClose={()=>{setOpen(false)}}
        onChange={e => onChange((e as any).target.value) }
        disabled={disabled}
        IconComponent={() =>isOpen ?  <CloseIcon style={{color: theme.theme.text.primary, height:'20px'}}/>: <KeyboardArrowDown style={{color: theme.theme.text.primary, height:'20px'}}/>}
      >
        {options.map((option) => (
          <MenuItem key={option.id} value={option.id}>
            {option.name.en ? getLocalizedProperty(locale, option.name): option.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default MultiSelectField;
